import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

const CELLS = 17;            // grid is CELLS x CELLS
const CELL_PX = 20;
// Tick interval (ms) per level — lower = faster.
const SPEEDS = [150, 125, 105, 88, 72, 58];
// Food needed to clear each level.
const GOAL = 8;

type Pt = { x: number; y: number };
type Dir = 'up' | 'down' | 'left' | 'right';

const DIRV: Record<Dir, Pt> = {
  up: { x: 0, y: -1 }, down: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 },
};

function randFood(snake: Pt[]): Pt {
  while (true) {
    const f = { x: Math.floor(Math.random() * CELLS), y: Math.floor(Math.random() * CELLS) };
    if (!snake.some(s => s.x === f.x && s.y === f.y)) return f;
  }
}

const GameSnake: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('snake');
  const [level, setLvl] = useState(prog.level || 1);
  const [snake, setSnake] = useState<Pt[]>([{ x: 8, y: 8 }]);
  const [food, setFood] = useState<Pt>(() => randFood([{ x: 8, y: 8 }]));
  const [eaten, setEaten] = useState(0);
  const [score, setScore] = useState(0);
  const [best] = useState(getHighScore('snake'));
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [cleared, setCleared] = useState(false);
  const dirRef = useRef<Dir>('right');
  const nextDirRef = useRef<Dir>('right');
  const savedRef = useRef(false);

  const speed = SPEEDS[Math.min(level - 1, SPEEDS.length - 1)];

  const reset = useCallback((lvl: number) => {
    const start = [{ x: 8, y: 8 }];
    setSnake(start);
    setFood(randFood(start));
    setEaten(0);
    setScore(0);
    setOver(false);
    setCleared(false);
    setRunning(true);
    dirRef.current = 'right';
    nextDirRef.current = 'right';
    savedRef.current = false;
    setLvl(lvl);
    setLevel('snake', lvl);
  }, []);

  const tick = useCallback(() => {
    setSnake(prev => {
      dirRef.current = nextDirRef.current;
      const d = DIRV[dirRef.current];
      const head = { x: prev[0].x + d.x, y: prev[0].y + d.y };
      // Wall or self collision → game over.
      if (head.x < 0 || head.y < 0 || head.x >= CELLS || head.y >= CELLS ||
          prev.some(s => s.x === head.x && s.y === head.y)) {
        setOver(true);
        setRunning(false);
        return prev;
      }
      const next = [head, ...prev];
      if (head.x === food.x && head.y === food.y) {
        sfx.score();
        setScore(s => s + 10 * level);
        setEaten(e => {
          const ne = e + 1;
          if (ne >= GOAL) {
            setCleared(true);
            setRunning(false);
          }
          return ne;
        });
        setFood(randFood(next));
      } else {
        next.pop();
      }
      return next;
    });
  }, [food, level]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(tick, speed);
    return () => clearInterval(id);
  }, [running, tick, speed]);

  // Persist on end.
  useEffect(() => {
    if ((over || cleared) && !savedRef.current) {
      savedRef.current = true;
      recordScore('snake', score, level);
      if (cleared) { unlockNext('snake', level); sfx.win(); } else sfx.lose();
    }
  }, [over, cleared, score, level]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const map: Record<string, Dir> = {
        ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
        w: 'up', s: 'down', a: 'left', d: 'right',
      };
      const nd = map[e.key];
      if (!nd) return;
      e.preventDefault();
      const cur = dirRef.current;
      // No 180° reversals.
      if ((nd === 'up' && cur === 'down') || (nd === 'down' && cur === 'up') ||
          (nd === 'left' && cur === 'right') || (nd === 'right' && cur === 'left')) return;
      nextDirRef.current = nd;
      if (!running && !over && !cleared) setRunning(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [running, over, cleared]);

  const steer = (nd: Dir) => {
    const cur = dirRef.current;
    if ((nd === 'up' && cur === 'down') || (nd === 'down' && cur === 'up') ||
        (nd === 'left' && cur === 'right') || (nd === 'right' && cur === 'left')) return;
    nextDirRef.current = nd;
    if (!running && !over && !cleared) setRunning(true);
  };

  return (
    <div className="gsnake">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{score}</b><i>score</i></span>
          <span className="gstat"><b>{Math.max(best, score)}</b><i>best</i></span>
          <span className="gstat"><b>{eaten}/{GOAL}</b><i>food</i></span>
        </div>
        <button className="game-restart" onClick={() => reset(level)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {SPEEDS.map((_, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('snake').unlockedLevel;
          return (
            <button key={lvl} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => unlocked && reset(lvl)} disabled={!unlocked}
              title={unlocked ? `Level ${lvl}` : 'Locked'}>
              {unlocked ? `Lvl ${lvl}` : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> points</div>
      )}

      <div
        className="gsnake-board"
        style={{ width: CELLS * CELL_PX, height: CELLS * CELL_PX }}
        onClick={() => { if (!running && !over && !cleared) setRunning(true); }}
      >
        {snake.map((s, i) => (
          <div key={i} className={`gsnake-seg ${i === 0 ? 'head' : ''}`}
            style={{ left: s.x * CELL_PX, top: s.y * CELL_PX, width: CELL_PX, height: CELL_PX }} />
        ))}
        <div className="gsnake-food" style={{ left: food.x * CELL_PX, top: food.y * CELL_PX, width: CELL_PX, height: CELL_PX }}>🍎</div>
        {!running && !over && !cleared && (
          <div className="gsnake-start">Press an arrow key / tap to start</div>
        )}
      </div>

      {/* On-screen D-pad for touch */}
      <div className="gsnake-dpad">
        <button onClick={() => steer('up')} aria-label="Up">▲</button>
        <div className="dpad-mid">
          <button onClick={() => steer('left')} aria-label="Left">◀</button>
          <button onClick={() => steer('down')} aria-label="Down">▼</button>
          <button onClick={() => steer('right')} aria-label="Right">▶</button>
        </div>
      </div>

      {(over || cleared) && (
        <div className="game-overlay">
          <div className="game-overlay-card">
            <div className="go-emoji">{cleared ? '🏆' : '💀'}</div>
            <h3>{cleared ? `Level ${level} cleared!` : 'Game over'}</h3>
            <p>You scored <b>{score}</b> points.{cleared && level < SPEEDS.length ? ' Next level unlocked.' : ''}</p>
            <div className="go-actions">
              {cleared && level < SPEEDS.length && (
                <button className="g-btn primary" onClick={() => reset(level + 1)}>Next level →</button>
              )}
              <button className="g-btn" onClick={() => reset(level)}>Play again</button>
              <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
            </div>
          </div>
        </div>
      )}

      <p className="game-hint">Arrow keys / WASD or the on-screen pad</p>
    </div>
  );
};

export default GameSnake;
